import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { migrationAPI, companiesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Loader2, DatabaseBackup, Download, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, XCircle, Eye, Undo2, Trash2, Play, ShieldAlert } from 'lucide-react';

const STATUS_META = {
  validated:           { label: 'Validated',           color: 'secondary' },
  validation_errors:   { label: 'Validation Errors',   color: 'destructive' },
  dry_run_completed:   { label: 'Dry-Run Done',        color: 'outline' },
  imported:            { label: 'Imported',            color: 'default' },
  partially_imported:  { label: 'Partially Imported',  color: 'secondary' },
  failed:              { label: 'Failed',              color: 'destructive' },
  rolled_back:         { label: 'Rolled Back',         color: 'outline' },
};

const DataMigration = () => {
  const { user } = useAuth();
  const isSuperAdmin = !user || user.role === 'super_admin';

  const [types, setTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  // Wizard state
  const [selectedType, setSelectedType] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(null); // upload result
  const [updateExisting, setUpdateExisting] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [applying, setApplying] = useState(false);

  // Detail / rollback dialogs
  const [detailBatch, setDetailBatch] = useState(null);
  const [rollbackConfirm, setRollbackConfirm] = useState(null);
  const [importConfirm, setImportConfirm] = useState(false);

  const tplMeta = types.find(t => t.key === selectedType);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [t, c, b] = await Promise.all([
        migrationAPI.types(),
        companiesAPI.getAll(),
        migrationAPI.list(),
      ]);
      setTypes(t.data);
      setCompanies(c.data);
      setBatches(b.data);
    } catch (e) {
      // Could be 403 if not super_admin
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isSuperAdmin) fetchData(); /* eslint-disable-next-line */ }, [isSuperAdmin]);

  if (!isSuperAdmin) return <Navigate to="/" replace />;
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const downloadTemplate = async () => {
    if (!selectedType) { toast.error('Select an import type first'); return; }
    try {
      const r = await migrationAPI.downloadTemplate(selectedType);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a'); a.href = url; a.download = `template_${selectedType}.xlsx`; a.click(); URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) { toast.error('Download failed'); }
  };

  const uploadFile = async () => {
    if (!selectedType || !file) { toast.error('Select import type and file'); return; }
    if (tplMeta?.company_scoped && !selectedCompany) { toast.error('This import is company-scoped — select a company'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('import_type', selectedType);
    if (selectedCompany) fd.append('company_id', selectedCompany);
    fd.append('file', file);
    try {
      const r = await migrationAPI.upload(fd);
      setCurrentBatch(r.data);
      if (r.data.failed_rows > 0) toast.warning(`Validated with ${r.data.failed_rows} errors — fix before importing`);
      else toast.success(`Validated: ${r.data.valid_rows}/${r.data.total_rows} rows ready`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const runImport = async (dryRun) => {
    if (!currentBatch) return;
    if (!dryRun && !window.confirm(`Apply ${currentBatch.valid_rows} rows to the database?${updateExisting ? ' Existing matching records will be UPDATED.' : ''}\n\nThis can be rolled back later.`)) return;
    setApplying(true);
    try {
      const r = await migrationAPI.apply(currentBatch.id, { dry_run: dryRun, update_existing: updateExisting, remarks });
      toast.success(`${dryRun ? 'Dry-run' : 'Import'} completed: ${r.data.inserted_count ?? r.data.would_insert ?? 0} inserted, ${r.data.updated_count ?? r.data.would_update ?? 0} updated, ${r.data.duplicate_rows ?? r.data.duplicates ?? 0} duplicates`);
      fetchData();
      if (!dryRun) setCurrentBatch(null);
      setImportConfirm(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    finally { setApplying(false); }
  };

  const downloadErrorReport = async (batchId) => {
    try {
      const r = await migrationAPI.errorReport(batchId);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a'); a.href = url; a.download = `errors_${batchId.slice(0, 8)}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || 'No errors to download'); }
  };

  const downloadSource = async (batchId, filename) => {
    try {
      const r = await migrationAPI.sourceFile(batchId);
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename || 'source.xlsx'; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const rollback = async () => {
    if (!rollbackConfirm) return;
    try {
      const r = await migrationAPI.rollback(rollbackConfirm.id);
      toast.success(`Rolled back: ${r.data.deleted} deleted, ${r.data.reverted} reverted`);
      setRollbackConfirm(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rollback failed'); }
  };

  const openDetail = async (batchId) => {
    try {
      const r = await migrationAPI.get(batchId);
      setDetailBatch(r.data);
    } catch (e) { toast.error('Failed to load details'); }
  };

  const companyName = (id) => companies.find(c => c.id === id)?.name || (id ? id.slice(0, 8) : '—');

  return (
    <div className="space-y-6" data-testid="data-migration-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><DatabaseBackup />Data Migration / Go-Live Import</h1>
          <p className="text-muted-foreground text-sm">Super-Admin only · Bulk import master &amp; opening data via CSV/XLSX with validation, dry-run, audit log and rollback.</p>
        </div>
        <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/50 max-w-md">
          <CardContent className="p-3 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
            <ShieldAlert size={14} className="mt-0.5 flex-shrink-0" />
            <div>Use this only during go-live or controlled mass updates. Every batch is recoverable via Rollback. Max file size <strong>50 MB</strong> · max <strong>25,000 rows</strong>.</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="wizard">
        <TabsList>
          <TabsTrigger value="wizard" data-testid="tab-wizard">New Import</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Import History ({batches.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="wizard" className="space-y-4">
          {/* Step 1+2 */}
          <Card>
            <CardHeader><CardTitle className="text-base">1. Select Import Type &amp; Company</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Import Type *</Label>
                <Select value={selectedType} onValueChange={(v) => { setSelectedType(v); setCurrentBatch(null); }}>
                  <SelectTrigger data-testid="select-import-type"><SelectValue placeholder="Choose what to import" /></SelectTrigger>
                  <SelectContent>{types.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
                {tplMeta && (
                  <p className="text-xs text-muted-foreground mt-1.5">{tplMeta.description}</p>
                )}
              </div>
              <div>
                <Label>Company {tplMeta?.company_scoped ? '*' : '(optional)'}</Label>
                <Select value={selectedCompany} onValueChange={setSelectedCompany} disabled={tplMeta && !tplMeta.company_scoped}>
                  <SelectTrigger data-testid="select-company"><SelectValue placeholder={tplMeta?.company_scoped ? 'Select target company' : '(Global — not required)'} /></SelectTrigger>
                  <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.country ? ` (${c.country})` : ''}</SelectItem>)}</SelectContent>
                </Select>
                {tplMeta && !tplMeta.company_scoped && <p className="text-xs text-muted-foreground mt-1.5">This is a global master — company is not required.</p>}
              </div>
            </CardContent>
          </Card>

          {/* Step 3 */}
          <Card>
            <CardHeader><CardTitle className="text-base">2. Download Template</CardTitle></CardHeader>
            <CardContent>
              <Button variant="outline" onClick={downloadTemplate} disabled={!selectedType} data-testid="download-template-btn">
                <Download size={14} className="mr-2" />Download {tplMeta?.label || 'Template'} (XLSX)
              </Button>
              {tplMeta && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                  <div className="border rounded p-2"><span className="text-muted-foreground">Unique Key:</span> <strong>{tplMeta.unique_key || '(none)'}</strong></div>
                  <div className="border rounded p-2"><span className="text-muted-foreground">Required Fields:</span> <strong>{tplMeta.required.join(', ') || '(none)'}</strong></div>
                  <div className="border rounded p-2"><span className="text-muted-foreground">Headers:</span> <strong>{tplMeta.headers.length}</strong> columns</div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 4 */}
          <Card>
            <CardHeader><CardTitle className="text-base">3. Upload File</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Label htmlFor="upload-input" className="inline-flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded cursor-pointer hover:bg-muted/50">
                <Upload size={16} />
                {file ? `${file.name} (${(file.size / 1024).toFixed(1)} KB)` : 'Choose CSV or XLSX file…'}
                <input id="upload-input" type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="file-input" />
              </Label>
              <Button onClick={uploadFile} disabled={!file || !selectedType || uploading} data-testid="upload-btn">
                {uploading && <Loader2 size={14} className="animate-spin mr-2" />}<FileSpreadsheet size={14} className="mr-2" />Upload &amp; Validate
              </Button>
            </CardContent>
          </Card>

          {/* Steps 5–7 */}
          {currentBatch && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center justify-between">
                  <span>4. Preview · Validate · Import</span>
                  <Badge variant={STATUS_META[currentBatch.status]?.color || 'secondary'} data-testid="batch-status-badge">{STATUS_META[currentBatch.status]?.label || currentBatch.status}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="border rounded p-3"><p className="text-xs text-muted-foreground">Total Rows</p><p className="text-2xl font-bold">{currentBatch.total_rows}</p></div>
                  <div className="border rounded p-3"><p className="text-xs text-muted-foreground">Valid</p><p className="text-2xl font-bold text-emerald-600">{currentBatch.valid_rows}</p></div>
                  <div className="border rounded p-3"><p className="text-xs text-muted-foreground">Failed</p><p className="text-2xl font-bold text-red-600">{currentBatch.failed_rows}</p></div>
                  <div className="border rounded p-3"><p className="text-xs text-muted-foreground">Duplicate (in file)</p><p className="text-2xl font-bold text-amber-600">{currentBatch.duplicate_rows || 0}</p></div>
                </div>

                {currentBatch.failed_rows > 0 && (
                  <div className="flex items-center justify-between bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded p-3">
                    <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                      <XCircle size={16} />
                      <span><strong>{currentBatch.failed_rows}</strong> rows failed validation. Fix and re-upload to continue.</span>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => downloadErrorReport(currentBatch.id)} data-testid="download-errors-btn"><Download size={12} className="mr-1" />Error Report</Button>
                  </div>
                )}

                {/* Preview */}
                {currentBatch.preview && currentBatch.preview.length > 0 && (
                  <div>
                    <Label className="text-xs">Preview (first 20 rows)</Label>
                    <div className="border rounded overflow-x-auto max-h-64">
                      <Table>
                        <TableHeader><TableRow>{Object.keys(currentBatch.preview[0]).map(h => <TableHead key={h} className="text-xs">{h}</TableHead>)}</TableRow></TableHeader>
                        <TableBody>
                          {currentBatch.preview.map((r, i) => (
                            <TableRow key={i}>
                              {Object.keys(currentBatch.preview[0]).map(h => <TableCell key={h} className="text-xs max-w-[200px] truncate">{r[h] || ''}</TableCell>)}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Options */}
                <div className="flex items-center gap-4 flex-wrap pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <Switch checked={updateExisting} onCheckedChange={setUpdateExisting} data-testid="update-existing-switch" />
                    <Label>Update existing records (matching by unique key)</Label>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks (optional)" data-testid="remarks-input" />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" onClick={() => runImport(true)} disabled={applying || currentBatch.valid_rows === 0} data-testid="dry-run-btn"><Eye size={14} className="mr-1" />Dry Run</Button>
                  <Button onClick={() => setImportConfirm(true)} disabled={applying || currentBatch.valid_rows === 0} data-testid="apply-import-btn"><Play size={14} className="mr-1" />Apply Import</Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Date</TableHead><TableHead>Import Type</TableHead><TableHead>Company</TableHead>
                  <TableHead>Rows</TableHead><TableHead>Status</TableHead><TableHead>Uploaded By</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {batches.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No imports yet.</TableCell></TableRow>}
                  {batches.map(b => (
                    <TableRow key={b.id} data-testid={`batch-row-${b.id}`}>
                      <TableCell className="text-xs">{new Date(b.uploaded_at).toLocaleString()}</TableCell>
                      <TableCell><Badge variant="outline">{types.find(t => t.key === b.import_type)?.label || b.import_type}</Badge></TableCell>
                      <TableCell className="text-xs">{b.company_id ? companyName(b.company_id) : '(global)'}</TableCell>
                      <TableCell className="text-xs">
                        <span className="text-emerald-600 font-medium">{b.inserted_count ?? b.valid_rows ?? 0}</span>
                        {' / '}
                        <span className="text-muted-foreground">{b.total_rows}</span>
                        {b.failed_rows > 0 && <span className="text-red-600"> · {b.failed_rows} failed</span>}
                      </TableCell>
                      <TableCell><Badge variant={STATUS_META[b.status]?.color || 'secondary'}>{STATUS_META[b.status]?.label || b.status}</Badge></TableCell>
                      <TableCell className="text-xs">{b.uploaded_by}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openDetail(b.id)} title="View details" data-testid={`view-batch-${b.id}`}><Eye size={12} /></Button>
                          {b.failed_rows > 0 && <Button size="sm" variant="ghost" onClick={() => downloadErrorReport(b.id)} title="Error report"><AlertTriangle size={12} className="text-red-600" /></Button>}
                          <Button size="sm" variant="ghost" onClick={() => downloadSource(b.id, b.source_filename)} title="Source file"><Download size={12} /></Button>
                          {(b.status === 'imported' || b.status === 'partially_imported') && (
                            <Button size="sm" variant="ghost" onClick={() => setRollbackConfirm(b)} title="Rollback" data-testid={`rollback-btn-${b.id}`}><Undo2 size={12} className="text-red-600" /></Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Apply Import Confirmation */}
      <Dialog open={importConfirm} onOpenChange={setImportConfirm}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle size={18} />Confirm Import</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <p>You are about to import <strong>{currentBatch?.valid_rows}</strong> rows into the live database.</p>
            <ul className="list-disc pl-5 text-muted-foreground">
              <li>Import type: <strong>{tplMeta?.label}</strong></li>
              <li>Company: <strong>{currentBatch?.company_id ? companyName(currentBatch.company_id) : 'Global'}</strong></li>
              <li>Update existing matching rows: <strong>{updateExisting ? 'YES' : 'NO (duplicates will be skipped)'}</strong></li>
            </ul>
            <p className="text-xs text-muted-foreground italic">This batch can be rolled back later from Import History.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportConfirm(false)}>Cancel</Button>
            <Button onClick={() => runImport(false)} disabled={applying} data-testid="confirm-import-btn">
              {applying && <Loader2 size={14} className="animate-spin mr-2" />}<CheckCircle2 size={14} className="mr-1" />Yes, Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback Confirmation */}
      <Dialog open={!!rollbackConfirm} onOpenChange={(o) => !o && setRollbackConfirm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600"><Trash2 size={18} />Rollback Import Batch</DialogTitle></DialogHeader>
          {rollbackConfirm && (
            <div className="space-y-2 text-sm">
              <p>This will <strong>permanently delete</strong> all records inserted by this batch and revert any updates to their previous state.</p>
              <div className="bg-red-50 dark:bg-red-950/30 rounded p-3 border border-red-200 dark:border-red-900/50">
                <p>Batch: <strong>{rollbackConfirm.import_type}</strong></p>
                <p>Uploaded: {new Date(rollbackConfirm.uploaded_at).toLocaleString()} by {rollbackConfirm.uploaded_by}</p>
                <p>Rows inserted: <strong>{rollbackConfirm.inserted_count || 0}</strong> · Updated: <strong>{rollbackConfirm.updated_count || 0}</strong></p>
              </div>
              <p className="text-xs text-amber-700 dark:text-amber-300">⚠ This action cannot be undone.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRollbackConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={rollback} data-testid="confirm-rollback-btn"><Undo2 size={14} className="mr-1" />Confirm Rollback</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailBatch} onOpenChange={(o) => !o && setDetailBatch(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Batch {detailBatch?.id?.slice(0, 8)} — {types.find(t => t.key === detailBatch?.import_type)?.label}</DialogTitle></DialogHeader>
          {detailBatch && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-muted-foreground">Status:</span> <Badge variant={STATUS_META[detailBatch.status]?.color}>{STATUS_META[detailBatch.status]?.label || detailBatch.status}</Badge></div>
                <div><span className="text-muted-foreground">Source:</span> <strong>{detailBatch.source_filename}</strong></div>
                <div><span className="text-muted-foreground">Uploaded:</span> {new Date(detailBatch.uploaded_at).toLocaleString()} by {detailBatch.uploaded_by}</div>
                {detailBatch.imported_at && <div><span className="text-muted-foreground">Imported:</span> {new Date(detailBatch.imported_at).toLocaleString()} by {detailBatch.imported_by}</div>}
                {detailBatch.rolled_back_at && <div className="col-span-2"><span className="text-muted-foreground">Rolled back:</span> {new Date(detailBatch.rolled_back_at).toLocaleString()} by {detailBatch.rolled_back_by}</div>}
                <div><span className="text-muted-foreground">Total:</span> <strong>{detailBatch.total_rows}</strong></div>
                <div><span className="text-muted-foreground">Valid:</span> <strong className="text-emerald-600">{detailBatch.valid_rows}</strong></div>
                <div><span className="text-muted-foreground">Inserted:</span> <strong className="text-emerald-600">{detailBatch.inserted_count || 0}</strong></div>
                <div><span className="text-muted-foreground">Updated:</span> <strong>{detailBatch.updated_count || 0}</strong></div>
                <div><span className="text-muted-foreground">Failed:</span> <strong className="text-red-600">{detailBatch.failed_rows || 0}</strong></div>
                <div><span className="text-muted-foreground">Duplicates:</span> <strong className="text-amber-600">{detailBatch.duplicate_rows || 0}</strong></div>
                {detailBatch.mode && <div><span className="text-muted-foreground">Mode:</span> <strong>{detailBatch.mode}</strong></div>}
                {detailBatch.remarks && <div className="col-span-2"><span className="text-muted-foreground">Remarks:</span> {detailBatch.remarks}</div>}
              </div>
              {(detailBatch.errors || []).length > 0 && (
                <div>
                  <Label className="text-xs">Validation Errors ({detailBatch.errors.length})</Label>
                  <div className="border rounded p-2 max-h-40 overflow-y-auto text-xs space-y-1">
                    {detailBatch.errors.slice(0, 50).map((e, i) => (
                      <div key={i} className="border-l-2 border-red-500 pl-2"><strong>Row {e.row}{e.key ? ` (${e.key})` : ''}:</strong> {e.errors.join('; ')}</div>
                    ))}
                  </div>
                </div>
              )}
              {(detailBatch.apply_errors || []).length > 0 && (
                <div>
                  <Label className="text-xs">Apply-time Errors ({detailBatch.apply_errors.length})</Label>
                  <div className="border rounded p-2 max-h-40 overflow-y-auto text-xs space-y-1">
                    {detailBatch.apply_errors.slice(0, 50).map((e, i) => (
                      <div key={i} className="border-l-2 border-red-500 pl-2"><strong>Row {e.row}{e.key ? ` (${e.key})` : ''}:</strong> {e.error}</div>
                    ))}
                  </div>
                </div>
              )}
              {(detailBatch.skipped_duplicates || []).length > 0 && (
                <div>
                  <Label className="text-xs">Skipped Duplicates ({detailBatch.skipped_duplicates.length})</Label>
                  <div className="border rounded p-2 max-h-40 overflow-y-auto text-xs space-y-1">
                    {detailBatch.skipped_duplicates.slice(0, 50).map((s, i) => (
                      <div key={i} className="border-l-2 border-amber-500 pl-2">Row {s.row} — <strong>{s.key}</strong> — {s.reason}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDetailBatch(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataMigration;
